import {PaymentConfig} from '../types';
import en from './locales/en.json';
import vi from './locales/vi.json';

export type PaymentLocale = 'en' | 'vi';

type TranslationParams = Record<string, string | number>;

type TranslationValue = string;
type IPPayload = {
    country_code?: string;
    country?: string;
    language?: string;
};

const URL_LOCALE_MAP: Record<string, PaymentLocale> = {
    en: 'en',
    vi: 'vi',
    vn: 'vi'
};

const TRANSLATIONS: Record<PaymentLocale, Record<string, TranslationValue>> = {
    en,
    vi
};

export function normalizeLocale(input?: string | null): PaymentLocale {
    const lowered = (input || '').toLowerCase();
    if (lowered.startsWith('vi') || lowered === 'vn') {
        return 'vi';
    }
    return 'en';
}

function detectLocaleFromNavigator(): PaymentLocale | null {
    if (typeof window === 'undefined') {
        return null;
    }
    const value = window.navigator?.language;
    if (!value || typeof value !== 'string') {
        return null;
    }
    return normalizeLocale(value);
}

export function detectLocaleFromUrl(url?: string): PaymentLocale | null {
    if (!url) return null;
    try {
        const parsed = new URL(url);
        const subdomain = parsed.hostname.split('.')[0]?.toLowerCase();
        if (subdomain && URL_LOCALE_MAP[subdomain]) {
            return URL_LOCALE_MAP[subdomain];
        }

        const firstPathSegment = parsed.pathname.split('/').filter(Boolean)[0]?.toLowerCase();
        if (firstPathSegment && URL_LOCALE_MAP[firstPathSegment]) {
            return URL_LOCALE_MAP[firstPathSegment];
        }
        return null;
    } catch {
        return null;
    }
}

function fromIPPayload(data: IPPayload): PaymentLocale | null {
    const countryCode = (data.country || '').toString().toUpperCase();
    if (countryCode === 'VN') {
        return 'vi';
    }

    const language = (data.language || '').toString().toLowerCase();
    if (language.includes('vi')) {
        return 'vi';
    }
    return null;
}

async function fetchLocaleFromIP(): Promise<PaymentLocale | null> {
    if (typeof fetch === 'undefined') return null;
    const endpoint = 'https://elsaspeak.com/api/get-ip';
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 2500) : null;
    try {
        const response = await fetch(endpoint, {
            method: 'GET',
            signal: controller?.signal
        });
        if (!response.ok) {
            return null;
        }
        const data = await response.json();
        const detected = fromIPPayload((data || {}) as IPPayload);
        if (detected) {
            return detected;
        }
    } catch {
        return null;
    }
    finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    }
    return null;
}

export function getInitialLocale(config: PaymentConfig): PaymentLocale {
    if (config.locale) {
        return normalizeLocale(config.locale);
    }

    if (typeof window !== 'undefined') {
        const fromUrl = detectLocaleFromUrl(window.location.href);
        if (fromUrl) return fromUrl;

        const browserLocale = detectLocaleFromNavigator();
        if (browserLocale) {
            return browserLocale;
        }
    }
    return 'en';
}

export async function resolveLocale(config: PaymentConfig): Promise<PaymentLocale> {
    if (config.locale) {
        return normalizeLocale(config.locale);
    }

    if (typeof window !== 'undefined') {
        const fromUrl = detectLocaleFromUrl(window.location.href);
        if (fromUrl) {
            return fromUrl;
        }
    }

    const fromIP = await fetchLocaleFromIP();
    if (fromIP) {
        return fromIP;
    }

    const browserLocale = detectLocaleFromNavigator();
    if (browserLocale) {
        return browserLocale;
    }
    return 'en';
}

export function t(locale: PaymentLocale, key: string, params?: TranslationParams): string {
    const lang = TRANSLATIONS[locale] || TRANSLATIONS.en;
    const template = lang[key] ?? TRANSLATIONS.en[key] ?? key;
    if (!params) {
        return template;
    }

    return template.replace(/\{\{(\w+)\}\}/g, (_, token: string) => {
        const value = params[token];
        return value === undefined || value === null ? '' : String(value);
    });
}

export function getLocalizedDefaultMethodText(methodId: string, locale: PaymentLocale): {label?: string; description?: string} {
    switch (methodId) {
        case 'stripe_js':
            return {
                label: t(locale, 'defaultStripeLabel'),
                description: t(locale, 'defaultStripeDescription')
            };
        case 'paypal':
            return {description: t(locale, 'defaultPaypalDescription')};
        case 'payoo':
            return {description: t(locale, 'defaultPayooDescription')};
        case 'momo':
            return {description: t(locale, 'defaultMomoDescription')};
        case 'shopee':
            return {description: t(locale, 'defaultShopeeDescription')};
        case 'fundiin':
            return {description: t(locale, 'defaultFundiinDescription')};
        case 'my2c2p':
            return {description: t(locale, 'default2c2pDescription')};
        default:
            return {};
    }
}
